import { useState } from 'react';

import { cn } from '../../../../utils/cn';
import {
  submitFeedback,
  removeFeedback,
  type FeedbackRating,
} from '../../../../services/api/feedback';
import { isWelcomeMessage, type ChatMessageItem } from '../types';

export const FeedbackButtons = ({
  sessionId,
  message,
  channel,
}: {
  sessionId: string;
  message: ChatMessageItem;
  channel?: string;
}) => {
  const [rating, setRating] = useState<FeedbackRating | null>(null);
  const [busy, setBusy] = useState(false);

  const isWelcomePlaceholder = isWelcomeMessage(message);
  if (isWelcomePlaceholder || !sessionId) return null;
  if (!message.content || !message.content.trim()) return null;

  const handleRate = async (next: FeedbackRating) => {
    if (busy) return;
    const isToggleOff = rating === next;
    const previous = rating;
    setBusy(true);
    setRating(isToggleOff ? null : next);
    try {
      if (isToggleOff) {
        await removeFeedback(sessionId, message.id);
      } else {
        await submitFeedback({
          sessionId,
          messageId: message.id,
          rating: next,
          channel: channel ?? null,
          responseIntentType:
            typeof message.responseMode === 'string' ? message.responseMode : null,
        });
      }
    } catch (err) {
      setRating(previous);

      console.warn('Failed to submit feedback', err);
    } finally {
      setBusy(false);
    }
  };

  const buttonBase =
    'inline-flex h-7 w-7 items-center justify-center rounded-sm border text-sm transition-colors duration-150';
  const inactive =
    'border-black/10 bg-white text-text-grey hover:border-brand-blue hover:text-brand-blue';
  const activeUp = 'border-emerald-500 bg-emerald-500 text-white';
  const activeDown = 'border-red-500 bg-red-500 text-white';

  return (
    <div className="flex items-center gap-1.5 pt-0.5">
      <button
        type="button"
        onClick={() => handleRate('up')}
        disabled={busy}
        aria-label="Bra svar"
        className={cn(buttonBase, rating === 'up' ? activeUp : inactive, busy && 'opacity-60')}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M7 11V21H4a1 1 0 01-1-1v-8a1 1 0 011-1h3z" />
          <path d="M7 11L12 3a3 3 0 013 3v3h5a2 2 0 012 2l-2 7a2 2 0 01-2 2h-9" />
        </svg>
      </button>
      <button
        type="button"
        onClick={() => handleRate('down')}
        disabled={busy}
        aria-label="Dåligt svar"
        className={cn(buttonBase, rating === 'down' ? activeDown : inactive, busy && 'opacity-60')}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 13V3h3a1 1 0 011 1v8a1 1 0 01-1 1h-3z" />
          <path d="M17 13L12 21a3 3 0 01-3-3v-3H4a2 2 0 01-2-2l2-7a2 2 0 012-2h9" />
        </svg>
      </button>
      {rating ? (
        <span className="text-[11px] text-text-grey/80">Tack för din feedback!</span>
      ) : null}
    </div>
  );
};
